import { Injectable } from "@angular/core";
import { IObjectObserver } from "../../services/IObjectObserver";
import { BonusactionDataModel, BonusActionViewModel, BonuspointsView, BonuspointsDataModel, BonusactionBenefitViewModel, BonusactionBenefitDataModel, BonusPointsLoadState } from "../../shared/models/bonussystem.model";
import { UserHelperService } from "../../services/userHelper.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ContentType, ImageOrientation } from "../../shared/contentTypes";
import { LikeService } from "../../services/like.service";
import { Observable } from "rxjs";
import * as moment from "moment";
import { WebVoucherService } from "../../services/web_voucher.service";
import { StorageHive, LocalKeys } from "../../services/valueStorage.service";
import { LocationService } from "../location/location.service";
import { BenefitRedemptionService } from "../../services/benefitRedemption.service";

@Injectable()
export class BonusSystemService {
  private _bonusObsServer: IObjectObserver<BonusactionDataModel>;
  private _bonusBenefitObsServer: IObjectObserver<BonusactionBenefitDataModel>;

  constructor(private _likeService: LikeService,
    private _webVoucherService: WebVoucherService,
    private _userHelperService: UserHelperService,
    private _objObsInjectHelper: CircularInjectionHelper,
    private _locationService: LocationService,
    private _benefitRedemptionService: BenefitRedemptionService) {
    this._bonusObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionDataModel>(ContentType.Bonusaction, _objObsInjectHelper);
    this._bonusBenefitObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionBenefitDataModel>(ContentType.BonusactionBenefit, _objObsInjectHelper);

  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._bonusObsServer.ObserveChanges;
  }

  public get BenefitObserveChanges(): Observable<(string | number)[]> {
    return this._bonusBenefitObsServer.ObserveChanges;
  }

  /**
 * Getting the list of BonusactionViewModel
 * @result return a promise that resolves a list of BonusactionViewModel
 */
  public GetBonusActionViewModel(): Promise<BonusActionViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<BonusActionViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      const currentDate: moment.Moment = moment();
      me._bonusObsServer.GetObjectsByFilter(b => !b.deleted && b.locations
        && currentDate.valueOf() >= b.start_at.valueOf()
        && ((!b.has_total_end_at || (b.has_total_end_at && currentDate.valueOf() <= b.total_end_at.valueOf()))))
        .then(async (bonusActions) => {
          try {
            let bonuspointsView: BonuspointsView = null;
            if (me._userHelperService.IsRegistered) {
              const bonusPointsStorage: BonuspointsDataModel = await me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.BonuspointsDataModel);
              if (bonusPointsStorage) {
                bonuspointsView = WebVoucherService.convertBonusPointStringToBonusPointsView(bonusPointsStorage);
              } else {
                const bonuspointsDataModel = await me._webVoucherService.GetBounspoints();
                me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.BonuspointsDataModel, bonuspointsDataModel);
                bonuspointsView = WebVoucherService.convertBonusPointStringToBonusPointsView(bonuspointsDataModel);
              }
            }
            me.mapBonusactionListToBonusactionViewModelList(bonusActions, bonuspointsView).then(resolve);
          } catch (e) {
            me._objObsInjectHelper.coreLogService.error("BonusSystemService.GetBonusActionViewModel.GetObjectsByFilter", "mapDataModelListToViewModelList/_webVoucherService.GetBounspoints", e);
            reject();
          }
        })
        .catch((e) => {
          me._objObsInjectHelper.coreLogService.error("BonusSystemService.GetBonusActionViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  /**
   * check if bonus points has been changed, then load it and upadate it agian
   */
  public async BonusActionInterval(bonusActionViewModels: BonusActionViewModel[]): Promise<void> {
    const me = this;
    try {
      if (me._userHelperService.IsRegistered) {
        const bonusPointsStorage: BonuspointsDataModel = await me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.BonuspointsDataModel)
        const lastPointAction = WebVoucherService.getLastPointActionFromBonusPointString(bonusPointsStorage);
        const bonuspointsDataModel = await me._webVoucherService.GetNewBounspoints(lastPointAction);
        // GetNewBounspoints liefert bewusst null, wenn der Nutzer keine Kartennummer hat
        if (bonuspointsDataModel && bonuspointsDataModel.bonuspoints && bonuspointsDataModel.bonuspoints.length > 0) {
          me.UpdateBonusPoints(bonusActionViewModels, WebVoucherService.convertBonusPointStringToBonusPointsView(bonuspointsDataModel));
          me._objObsInjectHelper.yourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.BonuspointsDataModel, bonuspointsDataModel);
        } else {
          // do nothing
        }
      } else {
        // do nothing
      }
    } catch (e) {
      me._objObsInjectHelper.coreLogService.error("BonusSystemService.bonusActionInterval", "try catch", e);
    }
  }

  /**
 * Update bonus points with proper state management
 */
  public UpdateBonusPoints(bonusActionViewModels: BonusActionViewModel[], bonuspointsView: BonuspointsView): void {
    const me = this;
    bonusActionViewModels.forEach(bonusActionViewModel => {
      if (!me._userHelperService.IsRegistered) {
        bonusActionViewModel.pointsLoadState = BonusPointsLoadState.NotAvailable;
        bonusActionViewModel.points = 0;
        return;
      }
      if (bonuspointsView && bonuspointsView.hasData && bonuspointsView.actions) {
        const userAction = bonuspointsView.actions.find(action => action.id === bonusActionViewModel.id);
        bonusActionViewModel.points = userAction ? userAction.points : 0;
        bonusActionViewModel.pointsLoadState = BonusPointsLoadState.Success;
      } else {
        bonusActionViewModel.pointsLoadState = BonusPointsLoadState.Error;
        // Keep existing points value to avoid showing 0 when there's an error
      }
    });
    me._benefitRedemptionService.recalculateBenefitPercentagesForList(bonusActionViewModels);
  }

  /**
   * Map a BonusActionDataModel to BonusActionViewModel with proper state management
   * @param model Get a BonusactionDataModel for mapping
   * @param bonuspointsView Bonus points data from server
   * @result return a Promise<BonusactionViewModel>
   */
  private async mapBonusactionToBonusactionViewModel(model: BonusactionDataModel, bonuspointsView: BonuspointsView): Promise<BonusActionViewModel> {
    const me = this;
    const viewModel = new BonusActionViewModel();
    viewModel.id = model.id;
    viewModel.labelDE = model.label_de;
    viewModel.descriptionDE = model.description_de.replace("\n", "<br>");
    viewModel.descriptionEN = model.description_en.replace("\n", "<br>");
    viewModel.image_photo = ContentType.Bonussystem + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    viewModel.locationsParticipated = model.locations_participation;
    viewModel.pointsInBasket = 0;
    viewModel.targetLocationIds = model.locations;
    try {
      if (model.locations_participation) {
        viewModel.locations = await me._locationService.GetBunosActionLocation(model.locations);
      } else {
        viewModel.locations = await me._locationService.GetBlackListBunosActionLocation(model.locations);
      }
    } catch (error) {
      me._objObsInjectHelper.coreLogService.error("BonusSystemDetailService.mapBonusactionToBonusActionDetailViewModel", "GetLocation try catch", error);
    }

    const benefitDataModels = await me._bonusBenefitObsServer.GetObjectsByFilter(b => b.bonusaction_id === model.id && !b.deleted);
    viewModel.benefits = [];

    viewModel.points = 0;
    viewModel.pointsLoadState = BonusPointsLoadState.NotLoaded;

    if (!me._userHelperService.IsRegistered) {
      viewModel.pointsLoadState = BonusPointsLoadState.NotAvailable;
    } else {
      try {
        if (bonuspointsView && bonuspointsView.hasData) {
          if (bonuspointsView.actions && bonuspointsView.actions.filter(action => action.id === model.id)[0]) {
            viewModel.points = bonuspointsView.actions.filter(action => action.id === model.id)[0].points;
          }
          viewModel.pointsLoadState = BonusPointsLoadState.Success;
        } else {
          viewModel.pointsLoadState = BonusPointsLoadState.Error;
          const errorCode = `GetBounspoints errorCode: ${bonuspointsView && bonuspointsView.errorCode ? bonuspointsView.errorCode : 'null'}`;
          const errorMessage = `errorMessage: ${bonuspointsView && bonuspointsView.errorMessage ? bonuspointsView.errorMessage : 'bonuspointsView is null'}`;
          me.handleErrorMsg(errorCode, "mapBonusactionToBonusactionViewModel", errorMessage);
        }
      } catch (error) {
        viewModel.pointsLoadState = BonusPointsLoadState.Error;
        me.handleErrorMsg(error, "mapBonusactionToBonusactionViewModel");
      }
    }

    // Map benefits after points are determined
    viewModel.benefits.push(...me.mapToBonusBenefitViewModelList(benefitDataModels, viewModel.points));

    // Set like status
    try {
      viewModel.like = await me._likeService.HasLike(ContentType.Bonussystem, model.id);
    } catch (error) {
      viewModel.like = false;
      me.handleErrorMsg(error, "mapBonusactionToBonusactionViewModel", "like service error");
    }
    return Promise.resolve(viewModel);
  }

  /**
  * map a list of BonusactionDataModel to a list of BonusactionViewModel
  * @param model Get a list of BonusactionDataModel for mapping
  * @result return a list of BonusactionViewModel
  */
  private mapBonusactionListToBonusactionViewModelList(modelList: BonusactionDataModel[], webvouvherViews: BonuspointsView): Promise<BonusActionViewModel[]> {
    const me = this;
    if (modelList) {
      return Promise.all(modelList.map((model) => me.mapBonusactionToBonusactionViewModel(model, webvouvherViews)));
    } else {
      return Promise.resolve(null);
    }
  }

  private mapToBonusBenefitViewModel(model: BonusactionBenefitDataModel, points: number): BonusactionBenefitViewModel {
    const viewModel = new BonusactionBenefitViewModel();
    viewModel.id = model.id;
    viewModel.product_id = model.product_id;
    viewModel.bonusaction_id = model.bonusaction_id;
    viewModel.loss_id = model.loss_id;
    viewModel.label_de = model.label_de;
    viewModel.cost = model.points;
    viewModel.isInBasket = false;
    viewModel.benefitpercantage = this._benefitRedemptionService.calculateBenefitPercentage(points, model.points);
    viewModel.image_photo = ContentType.Bonussystem_BenefitImage + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    return viewModel;
  }

  private mapToBonusBenefitViewModelList(modelList: BonusactionBenefitDataModel[], points: number): BonusactionBenefitViewModel[] {
    const me = this;
    if (modelList && modelList.length > 0) {
      const result: BonusactionBenefitViewModel[] = [];
      modelList.forEach(model => {
        result.push(me.mapToBonusBenefitViewModel(model, points));
      });
      return result;
    } else {
      return null;
    }
  }

  /**
   * Centralized error handling for mapping operations
   * @param error Error object or message
   * @param method Method name where error occurred
   * @param context Additional error context
   */
  private handleErrorMsg(error: any, method: string, context: string = ''): void {
    this._objObsInjectHelper.coreLogService.error(
      `BonusSystemService.${method}`,
      context || `Error in ${method}`,
      error
    );
  }
}
